<template>
  <div>
    <header-component />
    <div class="px-2 py-2 w-full">
      <div class="card">
        <div class="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
          <h3 class="mb-0">Stock Movements</h3>
          <div class="d-flex align-items-center flex-wrap gap-2">
            <select v-model="filters.branch_id" @change="fetch(1)" class="form-control form-control-sm" style="width:160px;">
              <option value="">All Branches</option>
              <option v-for="b in branches" :key="b.id" :value="b.id">{{ b.name }}</option>
            </select>
            <input v-model="filters.product_name" @input="onProductInput" type="text" placeholder="Product name..." class="form-control form-control-sm" style="width:160px;" />
            <select v-model="filters.movement_type" @change="fetch(1)" class="form-control form-control-sm" style="width:160px;">
              <option value="">All Types</option>
              <option v-for="(label, type) in TYPE_LABELS" :key="type" :value="type">{{ label }}</option>
            </select>
            <input v-model="filters.from_date" @change="fetch(1)" type="date" class="form-control form-control-sm" style="width:140px;" />
            <input v-model="filters.to_date" @change="fetch(1)" type="date" class="form-control form-control-sm" style="width:140px;" />
          </div>
        </div>

        <div class="table-responsive">
          <table class="table table-sm table-flush">
            <thead class="thead-light">
              <tr>
                <th>Date</th>
                <th>Branch</th>
                <th>Product</th>
                <th>Type</th>
                <th>Qty</th>
                <th>Reference</th>
                <th>Created By</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading"><td colspan="7" class="text-center py-3">Loading...</td></tr>
              <tr v-else-if="!rows.length"><td colspan="7" class="text-center text-muted py-3">No movements found.</td></tr>
              <tr v-for="row in rows" :key="row.id">
                <td class="text-xs">{{ formatDate(row.created_at) }}</td>
                <td class="text-xs">
                  <span
                    v-if="row.branch_name"
                    class="branch-badge"
                    :style="branchBadgeStyle(branchColorFor(row))"
                  >{{ row.branch_name }}</span>
                  <span v-else>—</span>
                </td>
                <td class="text-xs">{{ row.product_name }}</td>
                <td>
                  <span :class="['badge', TYPE_CLASSES[row.movement_type] || 'badge-secondary']" style="font-size:10px;">
                    {{ TYPE_LABELS[row.movement_type] || row.movement_type }}
                  </span>
                </td>
                <td class="text-xs font-weight-bold" :class="row.quantity >= 0 ? 'text-success' : 'text-danger'">
                  {{ row.quantity >= 0 ? '+' : '' }}{{ row.quantity }}
                </td>
                <td class="text-xs">{{ row.reference_type || '—' }}</td>
                <td class="text-xs">{{ row.created_by_name || '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="card-footer d-flex align-items-center justify-content-between">
          <span class="text-sm text-muted">{{ meta.total }} total</span>
          <div class="d-flex gap-2">
            <button class="btn btn-sm btn-outline-secondary" :disabled="meta.page <= 1" @click="fetch(meta.page - 1)">Prev</button>
            <span class="align-self-center text-sm">Page {{ meta.page }} / {{ meta.pages || 1 }}</span>
            <button class="btn btn-sm btn-outline-secondary" :disabled="meta.page >= meta.pages" @click="fetch(meta.page + 1)">Next</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import headerComponent from "../Components/HeaderComponent";
import { apiHelper } from "@/utils/helpers";
import endpoints from "@/utils/constants";

const BASE = endpoints.LARAVEL_BASE_URL;

const TYPE_LABELS = {
  sale:                'Sale',
  transfer_in:         'Transfer In',
  transfer_out:        'Transfer Out',
  opening_stock:       'Opening Stock',
  adjustment:          'Manual Correction',
  chalan_out:          'Chalan Out',
  chalan_in:           'Chalan In',
  chalan_out_reversal: 'Chalan Out Reversal',
  chalan_in_reversal:  'Chalan In Reversal',
  sr_chalan_in:        'SR Chalan In',
  sr_chalan_out:       'SR Chalan Out',
  damage:              'Damage / Spoilage',
  expired:             'Expired / Rusted',
  loss:                'Loss / Theft',
  surplus:             'Surplus Found',
};

const TYPE_CLASSES = {
  sale:                'badge-danger',
  transfer_in:         'badge-success',
  transfer_out:        'badge-warning',
  opening_stock:       'badge-primary',
  adjustment:          'badge-secondary',
  chalan_out:          'badge-warning',
  chalan_in:           'badge-info',
  chalan_out_reversal: 'badge-light',
  chalan_in_reversal:  'badge-light',
  sr_chalan_in:        'badge-info',
  sr_chalan_out:       'badge-warning',
  damage:              'badge-danger',
  expired:             'badge-danger',
  loss:                'badge-danger',
  surplus:             'badge-success',
};

export default {
  components: { 'header-component': headerComponent },
  data() {
    return {
      TYPE_LABELS,
      TYPE_CLASSES,
      branches: [],
      rows: [],
      loading: false,
      meta: { total: 0, page: 1, pages: 1, per_page: 50 },
      filters: {
        branch_id:     '',
        product_name:  '',
        movement_type: '',
        from_date:     '',
        to_date:       '',
      },
      productInputTimer: null,
    };
  },
  mounted() {
    apiHelper.call('get', BASE + '/api/v1/branches')
      .then(res => { this.branches = res.data.data || res.data; })
      .catch(() => {});
    this.fetch(1);
  },
  methods: {
    onProductInput() {
      clearTimeout(this.productInputTimer);
      this.productInputTimer = setTimeout(() => this.fetch(1), 400);
    },
    fetch(page) {
      this.loading = true;
      var p = new URLSearchParams({ page, per_page: this.meta.per_page });
      Object.entries(this.filters).forEach(function([k, v]) { if (v) p.set(k, v); });
      apiHelper.call('get', BASE + '/api/v1/stock-movements?' + p.toString())
        .then(res => {
          this.rows = res.data.data || [];
          this.meta = res.data.meta || this.meta;
        })
        .catch(() => {})
        .finally(() => { this.loading = false; });
    },
    formatDate(dt) {
      if (!dt) return '—';
      return new Date(dt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    },
    branchColorFor(row) {
      const match = this.branches.find(branch =>
        (row.branch_id && String(branch.id) === String(row.branch_id)) ||
        (row.branch_name && branch.name === row.branch_name)
      );
      return match ? match.color : null;
    },
    branchBadgeStyle(color) {
      if (!color) return { background: "#dbeafe", color: "#1e40af" };
      return { background: color, color: "#fff" };
    },
  },
};
</script>

<style scoped>
.branch-badge {
  display: inline-block;
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
  padding: 0.1rem 0.45rem;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.3;
  border-radius: 999px;
}
</style>
